import { inject, Injectable } from '@angular/core';
import { AimPredefinedProbe } from '@features/aim/aim-details/_core/types/aim-predefined-probe.interface';
import { Probe } from '@features/strain/strain-details/_core/types';
import { TextValue } from '@shared/types';
import { ApiHelper } from '../../core/api/api.helper';
import { ApiService } from '../../core/api/api.service';
import { HttpResponse } from '../../core/api/http-response.interface';
import { AutosuggestMapper } from './autosuggest.mapper';

@Injectable({
    providedIn: 'root',
})
export class AutosuggestApiService {
    private apiService = inject(ApiService);

    suggestUsers(payload: {
        searchValue: string;
        take: number;
        skip: number;
    }): Promise<HttpResponse<{ id: number; name: string; email: string }[]>> {
        return new Promise<HttpResponse<{ id: number; name: string; email: string }[]>>(resolve => {
            this.apiService.post(`autosuggest/suggest-users`, payload).subscribe({
                next: (res: any) => {
                    resolve(AutosuggestMapper.mapAutosuggestUsers(res));
                },
                error: err => {
                    ApiHelper.resolveError(resolve, err?.error?.message);
                    console.error(err);
                },
            });
        });
    }

    suggestOrganizations(payload: {
        searchValue: string;
        take: number;
        skip: number;
    }): Promise<HttpResponse<{ id: number; name: string }[]>> {
        return new Promise<HttpResponse<{ id: number; name: string }[]>>(resolve => {
            this.apiService.post(`autosuggest/suggest-organizations`, payload).subscribe({
                next: (res: any) => {
                    resolve(AutosuggestMapper.mapAutosuggestOrganizations(res));
                },
            });
        });
    }

    suggestLaboratories(payload: {
        searchValue: string;
        take: number;
        skip: number;
    }): Promise<HttpResponse<{ id: number; name: string }[]>> {
        return new Promise<HttpResponse<{ id: number; name: string }[]>>(resolve => {
            this.apiService.post(`autosuggest/suggest-laboratories`, payload).subscribe({
                next: (res: any) => {
                    resolve(AutosuggestMapper.mapAutosuggestLaboratories(res));
                },
            });
        });
    }

    suggestAssayDesigners(payload: {
        searchValue: string;
        take: number;
        skip: number;
    }): Promise<HttpResponse<{ id: number; email: string; name: string; isAvailable: boolean }[]>> {
        return new Promise<HttpResponse<{ id: number; email: string; name: string; isAvailable: boolean }[]>>(
            resolve => {
                this.apiService.post(`autosuggest/suggest-assay-designers`, payload).subscribe({
                    next: (res: any) => {
                        resolve(AutosuggestMapper.mapAutosuggestAssayDesigners(res));
                    },
                });
            }
        );
    }

    suggestStrains(payload: {
        searchValue: string;
        take: number;
        skip: number;
    }): Promise<HttpResponse<{ id: number; name: string }[]>> {
        return new Promise<HttpResponse<{ id: number; name: string }[]>>(resolve => {
            this.apiService.post(`autosuggest/suggest-strain`, payload).subscribe({
                next: (res: any) => {
                    resolve(AutosuggestMapper.mapSuggestStrains(res));
                },
            });
        });
    }

    suggestUserPredefinedProbesForStrains(payload: {
        searchValue: string;
        take: number;
        strainId: number;
        skip: number;
    }): Promise<HttpResponse<{ probes: Probe[] }>> {
        return new Promise<HttpResponse<{ probes: Probe[] }>>(resolve => {
            this.apiService.post(`autosuggest/suggest-user-predefined-probes-for-strains`, payload).subscribe({
                next: (res: any) => {
                    resolve(AutosuggestMapper.mapSuggestUserPredefinedProbesForStrains(res));
                },
                error: err => {
                    ApiHelper.resolveError(resolve, err?.error?.message);
                    console.error(err);
                },
            });
        });
    }

    suggestPredefinedProbes(payload: {
        searchValue: string;
        take: number;
        skip: number;
    }): Promise<HttpResponse<AimPredefinedProbe[]>> {
        return new Promise<HttpResponse<AimPredefinedProbe[]>>(resolve => {
            this.apiService.post(`autosuggest/suggest-predefined-probes`, payload).subscribe({
                next: (res: any) => {
                    resolve(AutosuggestMapper.mapSuggestPredefinedProbes(res));
                },
                error: err => {
                    ApiHelper.resolveError(resolve, err?.error?.message);
                    console.error(err);
                },
            });
        });
    }

    suggestPredefinedProbesForIncompatibilityAdd(payload: {
        searchValue: string;
        take: number;
        skip: number;
    }): Promise<HttpResponse<TextValue[]>> {
        return new Promise<HttpResponse<TextValue[]>>(resolve => {
            this.apiService.post(`autosuggest/suggest-predefined-probes-for-incompatibility`, payload).subscribe({
                next: (res: any) => {
                    resolve(AutosuggestMapper.mapSuggestPredefinedProbesForIncompatibility(res));
                },
                error: err => {
                    ApiHelper.resolveError(resolve, err?.error?.message);
                    console.error(err);
                },
            });
        });
    }

    suggestIncompatibleProbes(payload: {
        searchValue: string;
        take: number;
        skip: number;
    }): Promise<HttpResponse<TextValue[]>> {
        return new Promise<HttpResponse<TextValue[]>>(resolve => {
            this.apiService.post(`autosuggest/suggest-incompatible-probes`, payload).subscribe({
                next: (res: any) => {
                    resolve(AutosuggestMapper.mapSuggestIncompatibleProbes(res));
                },
                error: err => {
                    ApiHelper.resolveError(resolve, err?.error?.message);
                    console.error(err);
                },
            });
        });
    }

    suggestIncompatibleGroupNames(payload: {
        searchValue: string;
        take: number;
        skip: number;
    }): Promise<HttpResponse<TextValue[]>> {
        return new Promise<HttpResponse<TextValue[]>>(resolve => {
            this.apiService.post(`autosuggest/suggest-incompatible-group-names`, payload).subscribe({
                next: (res: any) => {
                    resolve(AutosuggestMapper.mapSuggestIncompatibleGroupNames(res));
                },
                error: err => {
                    ApiHelper.resolveError(resolve, err?.error?.message);
                    console.error(err);
                },
            });
        });
    }
}
